import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ScreenKey, resolveAllowedScreens } from 'constant/ScreenPermission';
import { ServerError, createApiError } from 'stores/ApiClientError';
import {
  StoredAccount,
  clearActiveSessionId,
  findAccountBySessionId,
  getAccounts,
  getActiveSessionId,
  removeAccountBySessionId,
  setActiveSessionId,
  upsertAccount,
} from 'stores/SessionStore';

export type { StoredAccount } from 'stores/SessionStore';

export const Role = {
  ADMIN: '管理者',
  GENERAL: '一般',
  MANAGER: '課長',
  BACK_OFFICE: '管理部門',
  DEVELOPER: '開発者',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Role = keyof typeof Role;

export type UserSession = {
  userId: string;
  mailAddress: string;
  userName: string;
  roles: Role[];
  allowedScreens: ScreenKey[];
};

/**
 * ログインAPI
 * @param userId - ユーザーID（必須, 最大300文字）
 * @param password - パスワード（必須, 最大300文字）
 *
 * ログイン成功後、セッションをアクティブにし、取得したユーザー情報を
 * ログイン済みアカウント一覧へ登録する（アカウント切り替え用）。
 */
export const login = async (userId: string, password: string): Promise<string> => {
  try {
    const response = await axios.post(KanzhongBackend.LOGIN, { userId, password });

    const sessionId: string = response.data.sessionId;
    startSession(sessionId);

    // ログイン済みアカウント一覧に登録（表示用メタ情報を取得して保存）
    try {
      const session = await fetchUserSession(sessionId);
      if (session) {
        upsertAccount({
          sessionId,
          userId: session.userId,
          userName: session.userName,
          mailAddress: session.mailAddress,
        });
      }
    } catch {
      // メタ情報の取得失敗はログイン自体を失敗させない
    }

    return sessionId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 指定したセッションIDでユーザーセッション情報を取得する。
 * 401 の場合は該当アカウントを一覧から除去し null を返す。
 */
const fetchUserSession = async (sessionId: string): Promise<UserSession | null> => {
  try {
    const response = await axios.get(KanzhongBackend.GET_USER_SESSION, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    const result = response.data.result;
    const roles: Role[] = result.roles;

    // 案3移行時: resolveAllowedScreens(roles) を result.allowedScreens に差し替えるだけ
    const allowedScreens = resolveAllowedScreens(roles);

    return {
      userId: result.userId,
      mailAddress: result.mailAddress,
      userName: result.userName,
      roles,
      allowedScreens,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      if (status === 401) {
        // 期限切れセッションは一覧からも除去する
        removeAccountBySessionId(sessionId);
        return null;
      }
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

export const getUserSession = async (): Promise<UserSession | null> => {
  const sessionId = getActiveSessionId();

  if (!sessionId) {
    return null;
  }

  const session = await fetchUserSession(sessionId);

  if (!session) {
    // アクティブセッションが無効になっていた場合はアクティブ指定を解除
    closeSession();
    return null;
  }

  return session;
};

export const startSession = (sessionId: string): void => {
  setActiveSessionId(sessionId);
};

export const closeSession = () => {
  clearActiveSessionId();
};

// ---------------------------------------------------------------------------
// アカウント切り替え
// ---------------------------------------------------------------------------

/**
 * ログイン済みアカウント一覧を取得する。
 * `active` は現在アクティブなアカウントかどうか。
 */
export const getLoggedInAccounts = (): (StoredAccount & { active: boolean })[] => {
  const activeSessionId = getActiveSessionId();
  return getAccounts().map((a) => ({ ...a, active: a.sessionId === activeSessionId }));
};

/**
 * 現在アクティブなアカウントを取得する。
 */
export const getActiveAccount = (): StoredAccount | undefined => {
  const activeSessionId = getActiveSessionId();
  if (!activeSessionId) {
    return undefined;
  }
  return findAccountBySessionId(activeSessionId);
};

/**
 * 指定したアカウント（sessionId）に切り替える。
 * アクティブセッションを切り替え、そのアカウントを一覧の先頭に移動する。
 */
export const switchAccount = (sessionId: string): void => {
  const account = findAccountBySessionId(sessionId);
  if (!account) {
    return;
  }
  setActiveSessionId(sessionId);
  // 直近に使ったアカウントを一番上へ
  upsertAccount(account);
};

/**
 * ログアウト（現在のアカウントを一覧から除去）する。
 * 他にログイン済みのアカウントが残っていれば、その一番上のアカウントに切り替えて
 * その sessionId を返す。残っていなければアクティブ指定を解除し null を返す。
 */
export const logoutActiveAccount = (): string | null => {
  const activeSessionId = getActiveSessionId();

  if (activeSessionId) {
    removeAccountBySessionId(activeSessionId);
  }
  clearActiveSessionId();

  const remaining = getAccounts();
  if (remaining.length > 0) {
    const next = remaining[0];
    setActiveSessionId(next.sessionId);
    return next.sessionId;
  }

  return null;
};
